import api from "@/lib/axiosConfig.ts";
import {
  CreateSectionDto,
  ImportImageDto,
  Section,
  SectionMetadata,
  Selection,
} from "@/lib/cms/section-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const baseUrl = "back/api/cms/section";

const getSections = async () => {
  const response = await api.get<Section[]>(baseUrl);
  return response.data;
};
export const useGetSections = () => {
  return useQuery({
    queryKey: ["sections"],
    queryFn: getSections,
  });
};

const getSectionById = async (id: string) => {
  const response = await api.get<Section>(`back/api/cms/section/${id}`);
  return response.data;
};
export const useGetSectionById = (id: string | undefined) => {
  return useQuery({
    queryKey: ["sections", id],
    queryFn: () => getSectionById(id!),
    enabled: !!id,
  });
};

const createSection = async (section: CreateSectionDto) => {
  const response = await api.post<Section>(baseUrl + "/create", section);
  return response.data;
};
export const useCreateSection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sections"] });
    },
  });
};

const updateSection = async (id: string, section: CreateSectionDto) => {
  const response = await api.patch<Section>(
    `back/api/cms/section/${id}`,
    section,
  );
  return response.data;
};
export const useUpdateSection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, section }: { id: string; section: CreateSectionDto }) =>
      updateSection(id, section),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sections"] });
    },
  });
};

const updateSectionsPositions = (sectionsIds: string[]) => {
  return api.patch<Section[]>(baseUrl + "/positions", sectionsIds);
};
export const useUpdateSectionsPositions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSectionsPositions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sections"] });
    },
  });
};

const getSectionSelections = async (id: string) => {
  return (await api.get<Selection[]>(baseUrl + `/${id}/selections`)).data;
};
export const useGetSectionSelections = (sectionId: string | undefined) => {
  return useQuery({
    queryKey: ["selection", sectionId],
    queryFn: () => getSectionSelections(sectionId!),
    enabled: !!sectionId,
  });
};

export const setSelectionItem = async (
  id: string,
  itemId: string,
  selected: boolean,
  itemType: "artwork" | "multipleprint",
) => {
  if (selected) {
    return (await api.post(baseUrl + `/${id}/${itemType}/add/${itemId}`)).data;
  }
  return (await api.post(baseUrl + `/${id}/${itemType}/remove/${itemId}`)).data;
};
export const useSetSelectionItem = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      selected,
      itemType,
    }: {
      itemId: string;
      selected: boolean;
      itemType: "artwork" | "multipleprint";
    }) => {
      if (!id) {
        throw new Error("Tried to set selection artwork but id is undefined");
      }
      return setSelectionItem(id, itemId, selected, itemType);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["selection", id] });
    },
  });
};

const getSectionMetadata = async (id: string) => {
  return (await api.get<SectionMetadata>(baseUrl + `/${id}/metadata`)).data;
};
export const useGetSectionMetadata = (id: string | undefined) => {
  return useQuery({
    queryKey: ["sectionMetadata", id],
    queryFn: () => getSectionMetadata(id!),
    enabled: !!id,
  });
};

const setSectionMetadata = async (
  id: string | undefined,
  sectionMetadata: SectionMetadata,
) => {
  if (!id) {
    throw new Error(
      "Tried to set section metadata but section id is undefined",
    );
  }
  return (
    await api.patch<SectionMetadata>(
      baseUrl + `/${id}/metadata`,
      sectionMetadata,
    )
  ).data;
};
export const useSetSectionMetadata = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionMetadata }: { sectionMetadata: SectionMetadata }) =>
      setSectionMetadata(id, sectionMetadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sectionMetadata"] });
    },
  });
};

const updateSelectionOrder = async (
  itemIds: string[],
  sectionId: string | undefined,
) => {
  if (!sectionId) {
    throw new Error(
      "Tried to update selection order but sectionId is undefined",
    );
  }
  return (
    await api.patch<Section[]>(baseUrl + `/${sectionId}/selections`, {
      itemIds,
    })
  ).data;
};
export const useUpdateSelectionOrder = (sectionId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (selection: Selection[]) => {
      const selectionIds = selection.map((s) => s.item.id);
      return updateSelectionOrder(selectionIds, sectionId);
    },

    onMutate: async (newSelection) => {
      await queryClient.cancelQueries({ queryKey: ["selection"] });
      const previousSelection = queryClient.getQueryData([
        "selection",
        sectionId,
      ]);
      queryClient.setQueryData(["selection", sectionId], () => newSelection);
      return previousSelection;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["selection", sectionId] });
    },
  });
};

const importImage = async (
  sectionId: string,
  importImageDto: ImportImageDto,
) => {
  return (
    await api.post(baseUrl + `/${sectionId}/import-image`, importImageDto)
  ).data;
};
export const useImportImage = (sectionId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (importImageDto: ImportImageDto) => {
      if (!sectionId) {
        throw new Error("Tried to import image but sectionId is undefined");
      }
      return importImage(sectionId, importImageDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["selection", sectionId] });
    },
  });
};

const deleteImportedImage = async (sectionId: string, imageId: string) => {
  return api.delete(baseUrl + `/${sectionId}/import-image/${imageId}`);
};
export const useDeleteImportedImage = (sectionId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) => {
      if (!sectionId) {
        throw new Error("Tried to delete image but sectionId is undefined");
      }
      return deleteImportedImage(sectionId, imageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["selection", sectionId] });
    },
  });
};

const editSelection = async (sectionId: string, importDto: ImportImageDto) => {
  return api.patch<Selection>(
    baseUrl + `/${sectionId}/import-image`,
    importDto,
  );
};
export const useEditSelection = (sectionId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ importDto }: { importDto: ImportImageDto }) => {
      if (!sectionId) {
        throw new Error("Tried to edit image but sectionId is undefined");
      }
      return editSelection(sectionId, importDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["selection", sectionId] });
    },
  });
};

const deleteSection = async (sectionId: string) => {
  return api.delete(baseUrl + `/${sectionId}`);
};
export const useDeleteSection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string | undefined) => {
      if (!sectionId) {
        throw new Error("Tried to delete section but sectionId is undefined");
      }
      return deleteSection(sectionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sections"] });
    },
  });
};

const getGlobalMode = async () => {
  try {
    const response = await api.get(baseUrl + `/global-mode`);
    return response.data;
  } catch (error) {
    console.error("Error getting global mode", error);
    return null;
  }
};

export const useGetGlobalMode = () => {
  return useQuery({
    queryKey: ["globalMode"],
    queryFn: getGlobalMode,
  });
};

const setGlobalSelection = async (isGlobal: boolean) => {
  return api.patch(baseUrl + `/toggle-global-mode`, { isGlobal });
};
export const useSetGlobalSelection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ isGlobal }: { isGlobal: boolean }) =>
      setGlobalSelection(isGlobal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sections"] });
      queryClient.invalidateQueries({ queryKey: ["globalMode"] });
    },
  });
};
